import { useQueryClient } from "@tanstack/react-query";
import { WS_EVENTS } from "@opus-dt/shared-types";
import { toast } from "@/components/ui/notify";
import { awaitingClassificationKey } from "@/features/closed-loop/hooks";
import { trackRecordKey } from "@/features/closed-loop/track-record/hooks";
import { useSocketEffect } from "./use-socket-effect";

// Alerta de agendamento de OP (broadcast por org). Evento literal emitido pelo
// worker (production-schedule); fora do enum WS_EVENTS por ser cross-cutting do worker.
const PRODUCTION_ORDER_ALERT = "production-order:alert";
interface OrderAlertPayload {
  code?: string | null;
  lineId?: string;
  kind?: "AUTO_STARTED" | "CANCELLED_BUSY" | "CANCELLED_MISSED";
}

// O ciclo fechado do cenário aplicado. Dois eventos literais do
// worker, fora do enum `WS_EVENTS` pelo mesmo motivo do alerta de OP: são cross-cutting do worker.
// ⚠️ As duas chaves do compromisso NÃO casam por prefixo entre si: a lista é
// `["applied-scenarios", lineId]` (plural) e o detalhe é `["applied-scenario", id]` (singular).
// Invalidar as duas é obrigatório, não redundante.
const APPLIED_SCENARIO_ADHERENCE = "applied-scenario:adherence";
const APPLIED_SCENARIO_SETTLED = "applied-scenario:settled";

interface AdherencePayload {
  appliedScenarioId?: string;
  lineId?: string;
}
interface SettledPayload {
  appliedScenarioId?: string;
  lineId?: string;
  lineName?: string | null;
  trigger?: "manual" | "automatic";
}

export function useRealtimeInvalidate(): void {
  const qc = useQueryClient();

  // Via useSocketEffect: religa os listeners quando o socket aparece/troca, em vez de
  // depender da ordem de declaração dos hooks no AppLayout (o socket é criado num efeito
  // que pode rodar depois deste). Antes, getSocket() null no mount deixava a invalidação
  // inerte enquanto o badge verde mentia "Tempo real". (#4)
  useSocketEffect(
    (socket) => {
      if (!socket) return;

      // Spec 078 — a cena 3D (["scene", lineId]) NÃO casa por prefixo com ["lines", …]; cada
      // handler que muda status, alarme, produção, OEE ou config invalida a cena explicitamente.
      const invalidateScene = (lineId?: string) => {
        void qc.invalidateQueries({ queryKey: lineId ? ["scene", lineId] : ["scene"] });
      };
      const invalidateLines = () => {
        void qc.invalidateQueries({ queryKey: ["lines"] });
        void qc.invalidateQueries({ queryKey: ["dashboard"] });
        void qc.invalidateQueries({ queryKey: ["attention"] });
        invalidateScene();
      };
      const invalidateAlarms = () => {
        void qc.invalidateQueries({ queryKey: ["alarms"] });
        void qc.invalidateQueries({ queryKey: ["notifications"] });
        void qc.invalidateQueries({ queryKey: ["dashboard"] });
        void qc.invalidateQueries({ queryKey: ["attention"] });
        invalidateScene();
      };
      const invalidateOee = (payload: { lineId?: string } | undefined) => {
        // ["oee", lineId] cobre a decomposição por máquina (["oee", lineId, "by-machine", …]).
        void qc.invalidateQueries({
          queryKey: payload?.lineId ? ["oee", payload.lineId] : ["oee"],
        });
        // Um reporte de refugo por máquina emite OEE_UPDATED; varre as perdas da
        // linha (LossesCard) e as máquinas (nomes/cards da decomposição) p/ os demais viewers.
        if (payload?.lineId) {
          void qc.invalidateQueries({ queryKey: ["lines", payload.lineId, "losses"] });
          void qc.invalidateQueries({ queryKey: ["lines", payload.lineId, "machines"] });
        }
        void qc.invalidateQueries({ queryKey: ["dashboard"] });
        invalidateScene(payload?.lineId);
      };
      const invalidateProduction = () => {
        void qc.invalidateQueries({ queryKey: ["production"] });
        void qc.invalidateQueries({ queryKey: ["lines"] });
        void qc.invalidateQueries({ queryKey: ["dashboard"] });
        // O SALDO de uma OP em andamento muda a cada apontamento, e é ele que o
        // objetivo "entregar no prazo" simula. Sem isto, o seletor ofereceria um saldo velho e a
        // resposta prometeria entrega sobre produção desatualizada.
        void qc.invalidateQueries({ queryKey: ["production-orders"] });
        // Spec 083 — o relatório de acompanhamento. Invalida por PREFIXO porque a chave carrega
        // período e agrupamento, e a combinação é infinita: enumerá-la seria garantir esquecer uma.
        // Ele é histórico, mas o período padrão inclui HOJE, e um apontamento feito agora precisa
        // aparecer sem o gestor recarregar a página.
        void qc.invalidateQueries({ queryKey: ["production-report"] });
        // Spec 084 — a batelada nasce e encerra com a execução; a lista da linha acompanha.
        void qc.invalidateQueries({ queryKey: ["batches"] });
        void qc.invalidateQueries({ queryKey: ["batch"] });
        invalidateScene();
      };
      // Config da máquina mudou (ex.: requiresOperator). O canvas é
      // alimentado por useLineMachines (["lines", lineId, "machines"]) — o prefixo
      // ["lines", lineId] cobre; sem lineId no payload, cai no invalidate amplo.
      const invalidateMachineConfig = (payload?: { lineId?: string }) => {
        void qc.invalidateQueries({
          queryKey: payload?.lineId ? ["lines", payload.lineId] : ["lines"],
        });
        invalidateScene(payload?.lineId);
      };

      const onOrderAlert = (payload: OrderAlertPayload | undefined) => {
        void qc.invalidateQueries({ queryKey: ["production-orders"] });
        void qc.invalidateQueries({ queryKey: ["lines"] });
        // O scheduler auto-iniciou/cancelou uma OP numa linha: refresca a página de detalhe da linha
        // aberta (produção ativa + OEE), senão ela só atualizaria no próximo refetch manual.
        if (payload?.lineId) {
          void qc.invalidateQueries({ queryKey: ["production", "active", payload.lineId] });
          // Spec 084 — o auto-start cria a batelada sem ninguém na tela.
          void qc.invalidateQueries({ queryKey: ["batches", payload.lineId] });
          invalidateOee({ lineId: payload.lineId });
        }
        const code = payload?.code ?? "OP";
        if (payload?.kind === "AUTO_STARTED") {
          toast.success(`Ordem ${code} iniciada automaticamente.`);
        } else if (payload?.kind === "CANCELLED_BUSY") {
          toast.error(`Ordem ${code} cancelada`, { description: "Linha ocupada no horário." });
        } else if (payload?.kind === "CANCELLED_MISSED") {
          toast.error(`Ordem ${code} cancelada`, { description: "Fora da janela de tolerância." });
        }
      };

      const invalidateAppliedScenario = (payload: AdherencePayload | undefined) => {
        if (payload?.appliedScenarioId) {
          void qc.invalidateQueries({ queryKey: ["applied-scenario", payload.appliedScenarioId] });
        }
        if (payload?.lineId) {
          void qc.invalidateQueries({ queryKey: ["applied-scenarios", payload.lineId] });
        }
      };

      const onScenarioSettled = (payload: SettledPayload | undefined) => {
        invalidateAppliedScenario(payload);
        // A apuração muda o painel: um compromisso deixou de estar em vigência.
        void qc.invalidateQueries({ queryKey: ["dashboard", "overview"] });
        // Spec 077 — e apurar é a ÚNICA coisa que põe item na fila de classificação. Sem esta
        // linha, quem está em `/ciclo-fechado` quando a varredura automática apura vê o toast e
        // continua com a fila vazia: `staleTime` de 30 s e sem `refetchOnWindowFocus` significam que
        // uma query montada não recarrega sozinha. Fila que esconde trabalho recém-chegado é a
        // mesma mentira que FR-042 proíbe.
        void qc.invalidateQueries({ queryKey: awaitingClassificationKey });
        // Spec 079 — a RÉGUA. Apurar acrescenta um compromisso ao universo dela: ele passa a contar
        // como "aguardando julgamento" na fábrica e na linha. `["closed-loop","track-record"]` NÃO
        // casa por prefixo com a chave acima, então precisa ser nomeada.
        void qc.invalidateQueries({ queryKey: trackRecordKey });
        // Só a apuração AUTOMÁTICA merece aviso: a manual acabou de ser pedida por quem está na
        // tela, e um toast repetiria em voz alta o que a pessoa já viu acontecer.
        if (payload?.trigger === "automatic") {
          toast.success("Compromisso apurado automaticamente", {
            description: payload.lineName
              ? `A vigência em ${payload.lineName} encerrou e o resultado já está registrado.`
              : "A vigência encerrou e o resultado já está registrado.",
          });
        }
      };

      socket.on(APPLIED_SCENARIO_ADHERENCE, invalidateAppliedScenario);
      socket.on(APPLIED_SCENARIO_SETTLED, onScenarioSettled);
      socket.on(WS_EVENTS.MACHINE_STATUS_CHANGED, invalidateLines);
      socket.on(WS_EVENTS.MACHINE_UPDATED, invalidateMachineConfig);
      socket.on(WS_EVENTS.ALARM_CREATED, invalidateAlarms);
      socket.on(WS_EVENTS.ALARM_STATE_CHANGED, invalidateAlarms);
      socket.on(WS_EVENTS.PRODUCTION_UPDATED, invalidateProduction);
      socket.on(WS_EVENTS.OEE_UPDATED, invalidateOee);
      socket.on(PRODUCTION_ORDER_ALERT, onOrderAlert);

      return () => {
        socket.off(APPLIED_SCENARIO_ADHERENCE, invalidateAppliedScenario);
        socket.off(APPLIED_SCENARIO_SETTLED, onScenarioSettled);
        socket.off(WS_EVENTS.MACHINE_STATUS_CHANGED, invalidateLines);
        socket.off(WS_EVENTS.MACHINE_UPDATED, invalidateMachineConfig);
        socket.off(WS_EVENTS.ALARM_CREATED, invalidateAlarms);
        socket.off(WS_EVENTS.ALARM_STATE_CHANGED, invalidateAlarms);
        socket.off(WS_EVENTS.PRODUCTION_UPDATED, invalidateProduction);
        socket.off(WS_EVENTS.OEE_UPDATED, invalidateOee);
        socket.off(PRODUCTION_ORDER_ALERT, onOrderAlert);
      };
    },
    [qc]
  );
}
